import { computed, inject, inputBinding, outputBinding, untracked } from '@angular/core';
import {
  ActionsStore,
  RecipeRunPollerService,
  RecipeRunUnfollowedError,
} from '@maximizer/actions/data-access';
import {
  Action,
  ACTIONS_SHOWN_FLAG,
  ActionStatus,
  isRunFinished,
  RecipeRun,
  RecipeRunStatus,
} from '@maximizer/actions/domain';
import { ActionCardComponent, ApproveEvent, RejectEvent } from '@maximizer/actions/ui';
import { LangGraphHandoff } from '@maximizer/ai/data-access';
import { FeatureService } from '@maximizer/core/shared/data-access';
import { catchError, distinctUntilChanged, map, of, Subscription } from 'rxjs';
import { isRecord } from '../../../classes/value-checks';
import { IQBoostOverrideView } from '../iqboost-chat-override';
import { IqboostActionsLinkComponent } from './iqboost-actions-link.component';
import {
  IQBoostHandoffRun,
  IQBoostHandoffRunRef,
  IQBoostHandoffRunStore,
} from './iqboost-handoff-run.store';
import {
  IQBoostHandoffLine,
  IqboostHandoffStatusComponent,
} from './iqboost-handoff-status.component';

// The same object for every handoff, so the dialog can show it once under an answer.
export const ACTIONS_LINK_VIEW: IQBoostOverrideView = {
  component: IqboostActionsLinkComponent,
  bindings: [],
};

export const RETRY_LATER_CODES: ReadonlySet<string> = new Set([
  'timeout',
  'unreachable',
  'upstream_failed',
  'redaction_failed',
]);

export type IQBoostUnfollowedStatus = 'late' | 'signIn' | 'failed';

export function unfollowedStatus(error: unknown): IQBoostUnfollowedStatus {
  if (error instanceof RecipeRunUnfollowedError) {
    if (error.reason === 'late') {
      return 'late';
    }
    if (error.reason === 'unauthorized') {
      return 'signIn';
    }
  }
  return 'failed';
}

// What a handoff started a run for, shown as one line under the answer.
export interface IQBoostHandedOff {
  // Null when the orchestrator could not read it.
  readonly name: string | null;
  // Null when the orchestrator could not start the run; `errorCode` says why.
  readonly run: IQBoostHandoffRunRef | null;
  readonly errorCode: string | null;
}

export function readRunRef(run: unknown): IQBoostHandoffRunRef | null {
  if (!isRecord(run)) {
    return null;
  }
  const { id, createdAt } = run;
  return typeof id === 'string' && typeof createdAt === 'string' ? { id, createdAt } : null;
}

export function readErrorCode(error: unknown): string | null {
  const code = isRecord(error) ? error['code'] : null;
  return typeof code === 'string' ? code : null;
}

export interface IQBoostHandoffRunStatuses<TStatus extends string> {
  // A run not heard from yet, or still queued or running.
  readonly working: TStatus;
  // A run that proposed cards the inbox could not load: its line offers to ask again.
  readonly loadFailed: TStatus;
  readonly ofRun: (run: RecipeRun) => TStatus;
  readonly ofUnfollowed: (error: unknown) => TStatus;
  // A run the orchestrator could not start, by the code it gave.
  readonly ofNotStarted: (errorCode: string | null) => TStatus;
}

// Made in an override's injection context: the runs its handoffs started, and the cards they proposed.
export class IQBoostHandoffRuns<TStatus extends string> {
  private readonly poller = inject(RecipeRunPollerService);
  private readonly inbox = inject(ActionsStore);
  private readonly feature = inject(FeatureService);
  private readonly known = computed(
    () => new Map(this.inbox.actions().map((action) => [action.id, action])),
  );
  // Each made once: the dialog renders a view again as soon as it is not the same object.
  private readonly views = new Map<string, IQBoostOverrideView>();
  // Kept when a run ends too: one that was lost track of is asked again only once shown anew.
  private readonly followers = new Map<string, Subscription>();
  // Asked for once while shown, by id and status: views are read at every render, and the inbox
  // drops a refresh still in flight when the same id comes again.
  private readonly refreshed = new Set<string>();

  constructor(
    private readonly store: IQBoostHandoffRunStore<TStatus>,
    private readonly statuses: IQBoostHandoffRunStatuses<TStatus>,
    private readonly line: IQBoostHandoffLine<TStatus>,
  ) {}

  // A line for each run the handoff started, or the run's cards in its place.
  handoffViews(
    handoff: LangGraphHandoff,
    handedOff: readonly IQBoostHandedOff[],
  ): readonly IQBoostOverrideView[] {
    const views = handedOff.flatMap((item, index) =>
      this.viewsOf(item.run, `${handoff.id}:${index}`, () => this.statusView(item)),
    );
    return this.awaitsReview(handedOff) ? [...views, ACTIONS_LINK_VIEW] : views;
  }

  statusOf(run: IQBoostHandoffRunRef): TStatus {
    const outcome = this.store.runOf(run.id);
    if (outcome?.actionIds.some((id) => this.failedToLoad(id))) {
      return this.statuses.loadFailed;
    }
    return outcome?.status ?? this.statuses.working;
  }

  // Asks again for the cards the inbox could not load: on a click, never on a render.
  reload(run: IQBoostHandoffRunRef): void {
    for (const id of this.store.runOf(run.id)?.actionIds ?? []) {
      if (this.failedToLoad(id)) {
        this.inbox.refresh(id);
      }
    }
  }

  // The run's cards once the inbox has every one; until then, the status view made under `key`.
  viewsOf(
    run: IQBoostHandoffRunRef | null,
    key: string,
    statusView: () => IQBoostOverrideView,
  ): readonly IQBoostOverrideView[] {
    const known = this.known();
    const outcome = run ? this.store.runOf(run.id) : undefined;
    const actionIds = outcome?.actionIds ?? [];
    // The template is rendering: what starts here may not write signals, nor be tracked by it.
    untracked(() => {
      if (run && !outcome?.final) {
        this.follow(run);
      }
      this.load(actionIds, known);
    });
    return actionIds.length > 0 && actionIds.every((id) => known.has(id))
      ? actionIds.map((id) => this.cardView(id))
      : [this.viewOf(key, statusView)];
  }

  // What was followed belonged to the conversation before; the one shown next follows its own.
  pauseAll(): void {
    for (const follower of this.followers.values()) {
      follower.unsubscribe();
    }
    this.followers.clear();
    this.refreshed.clear();
  }

  private follow({ id, createdAt }: IQBoostHandoffRunRef): void {
    if (this.followers.has(id)) {
      return;
    }
    this.followers.set(
      id,
      this.poller
        .follow(id, createdAt)
        .pipe(
          map((run) => this.outcomeOf(run)),
          distinctUntilChanged((previous, next) => previous.status === next.status),
          catchError((error: unknown) => of(this.unfollowedOutcome(error))),
        )
        .subscribe((outcome) => this.store.set(id, outcome)),
    );
  }

  private outcomeOf(run: RecipeRun): IQBoostHandoffRun<TStatus> {
    const actionIds = run.status === RecipeRunStatus.Proposal ? run.actionIds : [];
    return { status: this.statuses.ofRun(run), actionIds, final: isRunFinished(run.status) };
  }

  private unfollowedOutcome(error: unknown): IQBoostHandoffRun<TStatus> {
    // A run the API does not know will not turn up later.
    const final = error instanceof RecipeRunUnfollowedError && error.reason === 'notFound';
    return { status: this.statuses.ofUnfollowed(error), actionIds: [], final };
  }

  // The card opens on its payload, which a summary from the inbox's list does not carry.
  private load(actionIds: readonly string[], known: ReadonlyMap<string, Action>): void {
    const failed = this.inbox.payloadErrors();
    for (const id of actionIds) {
      const action = known.get(id);
      // The inbox drops a payload once the status moves on, so a new status asks again. A card that
      // could not be fetched waits for its Try again instead.
      const asked = action ? `${id}:${action.status}` : id;
      if (action?.payload == null && failed[id] === undefined && !this.refreshed.has(asked)) {
        this.refreshed.add(asked);
        this.inbox.refresh(id);
      }
    }
  }

  // The flag last: the views are read at every render, and most answers have no card waiting.
  private awaitsReview(handedOff: readonly IQBoostHandedOff[]): boolean {
    const known = this.known();
    const waiting = handedOff.some(
      ({ run }) =>
        run !== null &&
        (this.store.runOf(run.id)?.actionIds ?? []).some(
          (id) => known.get(id)?.status === ActionStatus.Pending,
        ),
    );
    return waiting && this.feature.isFeatureOn(ACTIONS_SHOWN_FLAG, false);
  }

  private failedToLoad(id: string): boolean {
    return !this.known().has(id) && this.inbox.payloadErrors()[id] !== undefined;
  }

  private statusView(item: IQBoostHandedOff): IQBoostOverrideView {
    const { run } = item;
    return {
      component: IqboostHandoffStatusComponent,
      bindings: [
        inputBinding('line', () => this.line),
        inputBinding('name', () => item.name),
        inputBinding('status', () =>
          run ? this.statusOf(run) : this.statuses.ofNotStarted(item.errorCode),
        ),
        outputBinding('retry', () => run && this.reload(run)),
      ],
    };
  }

  private cardView(id: string): IQBoostOverrideView {
    return this.viewOf(id, () => ({
      component: ActionCardComponent,
      bindings: [
        inputBinding('action', () => this.known().get(id)),
        inputBinding('expanded', () => true),
        inputBinding('payloadError', () => this.inbox.payloadErrors()[id] ?? null),
        outputBinding<ApproveEvent>('approve', (event) =>
          this.inbox.approve(event.id, event.payload),
        ),
        outputBinding<RejectEvent>('reject', (event) => this.inbox.reject(event.id, event.reason)),
        outputBinding<string>('retry', (actionId) => this.inbox.expand(actionId)),
      ],
    }));
  }

  private viewOf(key: string, make: () => IQBoostOverrideView): IQBoostOverrideView {
    let view = this.views.get(key);
    if (!view) {
      view = make();
      this.views.set(key, view);
    }
    return view;
  }
}
